import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SITE } from "@/config/site";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  ALL_DENIED,
  ALL_GRANTED,
  clearConsent,
  readConsent,
  saveConsent,
  subscribeConsent,
} from "@/lib/cookie-consent";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
      { name: "robots", content: "index, follow" },
      { title: "Cookie Policy · Surer Adda With Sanchayeeta" },
      { name: "description", content: "How Surer Adda With Sanchayeeta uses cookies and similar technologies, and how to manage your preferences." },
      { property: "og:title", content: "Cookie Policy · Surer Adda" },
      { property: "og:description", content: "Cookies we use, why we use them, and how to control them." },
      { property: "og:url", content: `${SITE.url}/cookies` },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/cookies` }],
  }),
  component: CookiesPage,
});

function CookiesPage() {
  const [analytics, setAnalytics] = useState(true);
  const [advertising, setAdvertising] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<string>("");
  const [hasChoice, setHasChoice] = useState(false);

  useEffect(() => {
    const apply = () => {
      const c = readConsent();
      if (c) {
        setAnalytics(c.analytics === "granted");
        setAdvertising(c.advertising === "granted");
        setUpdatedAt(c.updatedAt);
        setHasChoice(true);
      } else {
        setAnalytics(true);
        setAdvertising(true);
        setUpdatedAt("");
        setHasChoice(false);
      }
    };
    apply();
    return subscribeConsent(apply);
  }, []);

  const save = (next?: { analytics: boolean; advertising: boolean }) => {
    const a = next?.analytics ?? analytics;
    const ad = next?.advertising ?? advertising;
    const stored = saveConsent({
      analytics: a ? "granted" : "denied",
      advertising: ad ? "granted" : "denied",
    });
    setAnalytics(a); setAdvertising(ad); setUpdatedAt(stored.updatedAt); setHasChoice(true);
    toast.success("Cookie preferences saved");
  };

  const acceptAll = () => { save({ analytics: true, advertising: true }); };
  const rejectAll = () => { saveConsent(ALL_DENIED); setAnalytics(false); setAdvertising(false); setHasChoice(true); toast.success("All non-essential cookies rejected"); };
  const revoke = () => { clearConsent(); toast.success("Preferences cleared — banner re-opened"); };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <p className="font-script text-4xl text-primary">Cookie Policy</p>
        <p className="mt-1 text-sm text-muted-foreground">Last updated: June 2026</p>

        {/* Preferences control panel */}
        <section
          aria-labelledby="cookie-prefs"
          className="mt-8 rounded-lg border border-border/60 bg-card p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 id="cookie-prefs" className="text-lg font-semibold">Your preferences</h2>
            <p className="text-xs text-muted-foreground">
              {hasChoice && updatedAt
                ? `Last updated ${new Date(updatedAt).toLocaleString()}`
                : "No choice saved yet"}
            </p>
          </div>

          <div className="mt-4 space-y-3">
            <PrefRow
              id="ess"
              title="Essential"
              description="Required for authentication, sessions, and storing your consent. Always active."
              checked
              disabled
            />
            <PrefRow
              id="ana"
              title="Analytics"
              description="Google Analytics — page views, traffic sources, aggregate usage."
              checked={analytics}
              onChange={(v) => { setAnalytics(v); save({ analytics: v, advertising }); }}
            />
            <PrefRow
              id="ads"
              title="Advertising"
              description="Google Ads & AdSense — personalised ads and conversion measurement."
              checked={advertising}
              onChange={(v) => { setAdvertising(v); save({ analytics, advertising: v }); }}
            />
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button size="sm" onClick={acceptAll}>Accept all</Button>
            <Button size="sm" variant="outline" onClick={rejectAll}>Reject all (non-essential)</Button>
            <Button size="sm" variant="ghost" onClick={revoke}>Revoke & re-open banner</Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Toggle changes apply immediately — analytics and advertising scripts pick up the new
            choice on your next page load.
          </p>
        </section>

        <div className="prose prose-neutral mt-10 max-w-none text-foreground text-sm leading-relaxed">
          <p>
            This Cookie Policy explains how Surer Adda With Sanchayeeta ("we", "us") uses
            cookies and similar technologies when you visit our website.
          </p>

          <h2 className="mt-6 text-lg font-semibold">What are cookies?</h2>
          <p>
            Cookies are small text files stored on your device by your browser. They help
            websites remember information about your visit, like your login session or
            preferences.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Categories we use</h2>
          <ul className="list-disc space-y-2 pl-5">
            <li><strong>Essential</strong> — authentication, session, consent storage. Always on.</li>
            <li><strong>Analytics</strong> — Google Analytics. Loads only after you opt in.</li>
            <li><strong>Advertising</strong> — Google Ads & AdSense. Loads only after you opt in.</li>
          </ul>

          <h2 className="mt-6 text-lg font-semibold">Browser controls</h2>
          <p>
            You can also clear or block cookies through your browser settings. Note that
            disabling essential cookies may break parts of the site (e.g. signing in).
          </p>

          <h2 className="mt-6 text-lg font-semibold">Third parties</h2>
          <p>
            We use Google services (Ads &amp; Analytics) which may set their own cookies
            governed by Google's privacy policy.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Contact</h2>
          <p>
            Questions about cookies? Email{" "}
            <a className="text-primary hover:underline" href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a>.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function PrefRow({
  id, title, description, checked, disabled, onChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border/50 bg-background p-3">
      <div className="min-w-0">
        <Label htmlFor={`pref-${id}`} className="text-sm font-medium">{title}</Label>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        id={`pref-${id}`}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={`${title} cookies`}
      />
    </div>
  );
}
